import { resolve, type Source, SourceSchema } from '@/agent/refs';
import { ensureFeatureLayer, translateAttributes } from './shared/layers';
import { findLayer } from '@/map/layers/registry';
import type { Tool } from './types';

type Input = { source: Source };

export const describe: Tool<Input> = {
  name: 'describe',
  description:
    'Read the attributes of a feature, the user selection, or the first row of a feature_set. Coded-value fields are translated to their human labels.',
  input_schema: {
    type: 'object',
    properties: { source: SourceSchema },
    required: ['source'],
    additionalProperties: false,
  },
  execute: async ({ source }) => {
    const ref = await resolve(source, { needsGeometry: false });

    if (ref.feature) {
      const probe = findLayer(ref.feature.layer_id)?.probe;
      return {
        layer_id: ref.feature.layer_id,
        objectid: ref.feature.objectid,
        attributes: translateAttributes(ref.feature.attributes, probe),
      };
    }

    if (ref.feature_set && ref.feature_set.objectids.length > 0) {
      const layer = await ensureFeatureLayer(ref.feature_set.layer_id);
      const oid = ref.feature_set.objectids[0];
      const oidField = layer.objectIdField || 'objectid';
      const rs = await layer.queryFeatures({
        where: `${oidField} = ${oid}`,
        outFields: ['*'],
        returnGeometry: false,
        num: 1,
      });
      const f = rs.features[0];
      if (!f) throw new Error(`Feature ${oid} no longer in ${ref.feature_set.layer_id}`);
      const probe = findLayer(ref.feature_set.layer_id)?.probe;
      return {
        layer_id: ref.feature_set.layer_id,
        objectid: oid,
        attributes: translateAttributes(f.attributes ?? {}, probe),
        sample_of: ref.feature_set.count,
      };
    }

    throw new Error('describe: source has no readable attributes');
  },
};
